import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CalendarDays, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const REQUEST_TYPES = [
  { value: "workplace_chaplaincy", label: "Workplace Chaplaincy" },
  { value: "healthcare_hospice", label: "Healthcare & Hospice Support" },
  { value: "senior_living", label: "Senior Living / Board & Care" },
  { value: "training_workshop", label: "Training or Workshop" },
  { value: "organizational_consultation", label: "Organizational Consultation" },
  { value: "employee_support", label: "Employee Support" },
  { value: "pastoral_grief_counseling", label: "Pastoral Grief Counseling" },
  { value: "other", label: "Other" },
];

const initial = {
  first_name: "", last_name: "", email: "", phone: "", organization: "", role: "",
  request_type: "organizational_consultation", preferred_contact: "email",
  preferred_date: "", preferred_time: "flexible", message: "",
};

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

export default function SchedulePage() {
  const [params] = useSearchParams();
  const intent = (params.get("intent") || "").toLowerCase(); // e.g. "proposal"
  const duration = params.get("duration") || ""; // e.g. "15"
  const isProposal = intent === "proposal";

  usePageMeta({
    title: isProposal
      ? "Request a Workforce Care Proposal | California Corporate Chaplains"
      : "Schedule a Business Consultation | California Corporate Chaplains",
    description: isProposal
      ? "Request a tailored Workforce Care Proposal from California Corporate Chaplains. Confidential, non-clinical workplace chaplaincy for Northern California employers."
      : "Request a brief 15-minute business consultation with California Corporate Chaplains. Confidential, non-clinical, and designed for Northern California business owners.",
  });

  // Compose a non-identifying context prefix for the message field so the
  // existing admin inbox/bookings workflow sees what the visitor was offered,
  // without changing the backend model or admin UI.
  const contextPrefix = (() => {
    const parts = [];
    if (duration === "15") parts.push("Requesting a 15-minute business consultation.");
    if (isProposal) parts.push("Requesting a Workforce Care Proposal — please include an approximate employee count, primary location, and the services of interest in your reply.");
    return parts.join(" ");
  })();

  const [form, setForm] = useState(() => {
    const type = params.get("type");
    const baseMsg = contextPrefix ? `${contextPrefix}\n\n` : "";
    return {
      ...initial,
      request_type: REQUEST_TYPES.some((r) => r.value === type) ? type : initial.request_type,
      message: baseMsg,
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    const type = params.get("type");
    if (type && REQUEST_TYPES.some((r) => r.value === type)) setForm((p) => ({ ...p, request_type: type }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.post("/organizational-bookings", payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="schedule-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6"><CheckCircle2 className="w-7 h-7" /></div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Thank you.</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Your request has been received by California Corporate Chaplains. A member of our team will follow up using your preferred contact method.</p>
          <p className="mt-3 text-sm text-slate-500">Submission is a request — appointments are confirmed only once our team responds.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Back to home</Link>
            <Link to="/services" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">Explore services</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="schedule-page">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3" data-testid="schedule-eyebrow"><CalendarDays className="inline w-3.5 h-3.5 mr-1.5" />{isProposal ? "Workforce care proposal" : (duration === "15" ? "15-minute business consultation" : "Schedule a conversation")}</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-tight" data-testid="schedule-heading">{isProposal ? "Request a Workforce Care Proposal" : (duration === "15" ? "Schedule a 15-Minute Business Consultation" : "Request a Consultation")}</h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        {isProposal
          ? "Tell us about your organization so we can prepare a proposal that fits. Proposals are customized to each workforce; we do not publish fixed pricing."
          : "Tell us how to reach you and what you'd like to discuss. A member of our team will follow up to discuss what fits your organization."}
      </p>
      <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 leading-relaxed" data-testid="phi-notice">
        <strong>Please do not submit</strong> confidential medical information, diagnoses, protected health information, or other sensitive information through this general organizational form.
      </div>
      <form onSubmit={submit} data-testid="schedule-form" className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First name" required><input required data-testid="input-first-name" value={form.first_name} onChange={update("first_name")} className={inputCls} /></Field>
          <Field label="Last name" required><input required data-testid="input-last-name" value={form.last_name} onChange={update("last_name")} className={inputCls} /></Field>
        </div>
        <Field label="Email" required><input required type="email" data-testid="input-email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Phone"><input type="tel" data-testid="input-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
          <Field label="Organization"><input data-testid="input-organization" value={form.organization} onChange={update("organization")} className={inputCls} /></Field>
        </div>
        <Field label="Job title / role"><input data-testid="input-role" value={form.role} onChange={update("role")} className={inputCls} /></Field>
        <Field label="Request type" required>
          <select required data-testid="select-request-type" value={form.request_type} onChange={update("request_type")} className={inputCls}>
            {REQUEST_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Preferred contact" required>
            <select required data-testid="select-preferred-contact" value={form.preferred_contact} onChange={update("preferred_contact")} className={inputCls}>
              <option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option>
            </select>
          </Field>
          <Field label="Preferred date"><input type="date" data-testid="input-preferred-date" value={form.preferred_date} onChange={update("preferred_date")} className={inputCls} /></Field>
        </div>
        <Field label="Preferred time">
          <select data-testid="select-preferred-time" value={form.preferred_time} onChange={update("preferred_time")} className={inputCls}>
            <option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="evening">Evening</option><option value="flexible">Flexible</option>
          </select>
        </Field>
        <Field label="Message"><textarea data-testid="textarea-message" rows={4} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="A brief note about what you'd like to discuss (optional)." /></Field>
        <p className="text-xs text-slate-500 leading-relaxed">Submission is a <strong>request</strong> and does not guarantee a confirmed appointment until California Corporate Chaplains responds.</p>
        <button type="submit" disabled={submitting} data-testid="submit-schedule" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
          {submitting ? "Sending..." : "Send Request"} <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Encrypted in transit. Handled by our team, not sent to any employer.</p>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label} {required && <span className="text-[#4A6FA5]">*</span>}</span>
      {children}
    </label>
  );
}
